import { getFullUrl } from 'hubspot-url-utils';
import PortalIdParser from 'PortalIdParser';
import memoize from 'transmute/memoize';

/**
 * Update this interface if a new replacement data source is needed.
 */

/**
 * This function is to hold the list of all the objectTypeName patterns that are supported for interpolation.
 * If there is a need for a new object type name pattern in future, that can be added to this array.
 * TODO: Added new patterns with underscores, so that they do not interfere with the field key interpolation
 * Remove original patterns once these changes are communicated to the teams using this.
 */
const getObjectTypeReplacements = memoize(objectTypeDefinition => {
  var _objectTypeDefinition, _objectTypeDefinition2;
  return [{
    pattern: /\{\{objectTypeName\}\}|\{\{__objectTypeName__\}\}/g,
    replacement: (_objectTypeDefinition = objectTypeDefinition.get('singularForm')) === null || _objectTypeDefinition === void 0 ? void 0 : _objectTypeDefinition.toLowerCase()
  }, {
    pattern: /\{\{objectTypeNamePlural\}\}|\{\{__objectTypeNamePlural__\}\}/g,
    replacement: (_objectTypeDefinition2 = objectTypeDefinition.get('pluralForm')) === null || _objectTypeDefinition2 === void 0 ? void 0 : _objectTypeDefinition2.toLowerCase()
  }, {
    pattern: /\{\{objectTypeNameCapitalized\}\}|\{\{__objectTypeNameCapitalized__\}\}/g,
    replacement: objectTypeDefinition.get('singularForm')
  }, {
    pattern: /\{\{objectTypeNamePluralAndCapitalized\}\}|\{\{__objectTypeNamePluralAndCapitalized__\}\}/g,
    replacement: objectTypeDefinition.get('pluralForm')
  }];
});

/**
 * This function is to hold the appName replacement that is supported for interpolation.
 * If there is a need for a new application name pattern in future, that can be added to this array.
 * TODO: Added a new pattern with underscores, so that they do not interfere with the field key interpolation
 * Remove the original pattern once these changes are communicated to the teams using this.
 */
const getAppNameReplacement = appName => [{
  pattern: /\{\{appName\}\}|\{\{__appName__\}\}/g,
  replacement: appName
}];

/**
 * This function is to hold all the generic replacements which don't need any source data for replacement.
 * If there is a need for a new generic pattern, that can be added to this array.
 * TODO: Added a new pattern with underscores, so that they do not interfere with the field key interpolation
 * Remove the original pattern once these changes are communicated to the teams using this.
 */
const getGenericReplacements = () => {
  var _PortalIdParser$get;
  return [{
    pattern: /\{portalId\}|\{\{__portalId__\}\}/g,
    replacement: (_PortalIdParser$get = PortalIdParser.get()) === null || _PortalIdParser$get === void 0 ? void 0 : _PortalIdParser$get.toString()
  }, {
    pattern: /href="https:\/\/app\.hubspot\.com/g,
    replacement: `href="${getFullUrl('app')}`
  }];
};

/**
 * This function returns a map of all the supported regex patterns and their replacements.
 * If there is a need for a new pattern in future, identify if the existing data sources has the replacement value.
 * If so, update the corresponding helper function to include the regex and replacement.
 * If not, add a new helper function and append its replacement map conditionally to the return array.
 */
const getReplacementsMap = memoize(replacementSource => {
  const {
    objectTypeDefinition,
    appName
  } = replacementSource;
  return [...(!!objectTypeDefinition && objectTypeDefinition.get('id') ? getObjectTypeReplacements(objectTypeDefinition) : []), ...(appName ? getAppNameReplacement(appName) : []), ...getGenericReplacements()];
});

/**
 * This method is to interpolate text with the required replacements
 */
export const applyInterpolation = memoize((text, replacementSource) => {
  let interpolatedText = text;
  if (interpolatedText) {
    getReplacementsMap(replacementSource).forEach(({
      pattern,
      replacement
    }) => {
      if (replacement) {
        interpolatedText = interpolatedText.replaceAll(pattern, replacement);
      }
    });
  }
  return interpolatedText;
},
/*
 * Adding the hash function to fix a weird issue happening only on dev environment
 * and only in actions with more than 8 entries(description and fields) in the memoization hash.
 * This issue is happening in transmute/memoize
 * Related issue: https://github.com/facebook/react/issues/3469
 */
(text, {
  objectTypeDefinition,
  appName
}) => `${text}-${objectTypeDefinition && objectTypeDefinition.get('id')}-${appName}`);